/**
 * Ad monetization helpers.
 *
 * Web  → Google AdSense, loaded only after the visitor accepts optional cookies
 * Native → Google AdMob via @capacitor-community/admob (interstitials)
 *
 */
import { Capacitor } from "@capacitor/core";

export const COOKIE_CONSENT_KEY = "cookie-consent";
export const COOKIE_CONSENT_EVENT = "cookie-consent-change";

// ─── AdMob interstitial Ad Unit ID (production) ───────────────────────────────
// Format: ca-app-pub-XXXXXXXXXXXXXXXX/YYYYYYYYYY
export const ADMOB_INTERSTITIAL_AD_UNIT_ID =
  "ca-app-pub-3341285578140383/3482735962";

// ─── AdSense publisher client ID (production) ────────────────────────────────
// Format: ca-pub-XXXXXXXXXXXXXXXX
export const ADSENSE_CLIENT_ID = "ca-pub-3341285578140383";

// ─── AdSense banner ad slot ID (production) ───────────────────────────────────
export const ADSENSE_BANNER_SLOT_ID = "2178815007";

/** True when the banner slot is still the placeholder and must not be requested. */
export const isAdSenseBannerConfigured = (slot: string): boolean =>
  /^\d{6,}$/.test(slot) && slot !== "0000000000";

export const hasAdvertisingConsent = (): boolean =>
  typeof window !== "undefined" &&
  window.localStorage.getItem(COOKIE_CONSENT_KEY) === "accepted";

let adsenseLoading: Promise<void> | null = null;

/** Load the AdSense library only after an affirmative cookie choice. */
export const loadAdSense = async (): Promise<void> => {
  if (Capacitor.isNativePlatform() || !hasAdvertisingConsent()) return;
  if (document.querySelector<HTMLScriptElement>('script[data-smash-adsense="true"]')) return;
  if (adsenseLoading) return adsenseLoading;

  adsenseLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.dataset.smashAdsense = "true";
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("AdSense failed to load"));
    document.head.appendChild(script);
  }).catch((error) => {
    adsenseLoading = null;
    throw error;
  });

  return adsenseLoading;
};

let admobReady = false;
let interstitialPreparing: Promise<void> | null = null;

/** Initialize AdMob once at app boot — no-op on web. */
export const initAds = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.initialize({
      initializeForTesting: false,
    });
    admobReady = true;
    // Warm up the first interstitial so it's ready when the counter fires.
    void prepareInterstitial();
  } catch (err) {
    // AdMob not available (e.g. missing native module) — fail silently.
    void err;
  }
};

const prepareInterstitial = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform() || !admobReady) return;
  if (interstitialPreparing) return interstitialPreparing;
  interstitialPreparing = (async () => {
    try {
      const { AdMob } = await import("@capacitor-community/admob");
      await AdMob.prepareInterstitial({
        adId: ADMOB_INTERSTITIAL_AD_UNIT_ID,
      });
    } catch (err) {
      void err;
    } finally {
      interstitialPreparing = null;
    }
  })();
  return interstitialPreparing;
};

/**
 * Show an interstitial ad on native platforms.
 * Returns true if an ad was displayed, false otherwise (including on web).
 */
export const showInterstitialAd = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform() || !admobReady) return false;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.showInterstitial();
    void trackAdEvent("filled", ADMOB_INTERSTITIAL_AD_UNIT_ID);
    // Pre-load the next one for the following trigger.
    void prepareInterstitial();
    return true;
  } catch (err) {
    void err;
    void trackAdEvent("unfilled", ADMOB_INTERSTITIAL_AD_UNIT_ID);
    // Try to recover for next time.
    void prepareInterstitial();
    return false;
  }
};

export const isNativeAdsPlatform = (): boolean => Capacitor.isNativePlatform();
// ─── Ad performance tracking ─────────────────────────────────────────────────
export type AdEventType = "request" | "filled" | "unfilled" | "click";

/**
 * Record an ad event so the admin dashboard can show how the banner performs.
 * Fire-and-forget: tracking must never break rendering.
 */
export const trackAdEvent = async (
  eventType: AdEventType,
  slot: string,
): Promise<void> => {
  if (typeof window === "undefined") return;
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.auth.getSession();
    await supabase.from("ad_events").insert({
      event_type: eventType,
      slot,
      page_path: window.location.pathname.slice(0, 200),
      platform: Capacitor.isNativePlatform() ? "native" : "web",
      user_id: data.session?.user.id ?? null,
    });
  } catch (err) {
    void err;
  }
};
